import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useCart, formatINR } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { reduceMotion } = useTheme();
  const { addItem } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const ref = useRef(null);
  const liked = isWishlisted(product.id);

  const rafRef = useRef(null);
  const onMove = (e) => {
    if (reduceMotion) return;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg) translateY(-2px)`;
    });
  };
  const onLeave = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    const el = ref.current; if (el) el.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };

  const onAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (product.coming_soon) { toast.info("Coming soon. Stay tuned."); return; }
    addItem(product);
    toast.success(`${product.name} added to cart.`, { duration: 1800 });
  };

  const angle = 120 + ((index * 35) % 120);
  const tagStyle = (tag = "") => {
    const t = tag.toUpperCase();
    if (t.includes("LIMITED")) return { color: "#C0A0FF", shadow: "0 0 12px rgba(192,160,255,0.55)" };
    if (t.includes("BESTSELLER")) return { color: "#FFD7B5", shadow: "0 0 12px rgba(255,215,181,0.55)" };
    return { color: "#00AAFF", shadow: "0 0 12px rgba(0,170,255,0.55)" };
  };
  const ts = tagStyle(product.tag);

  return (
    <Link
      to={`/product/${product.id}`}
      className="block"
      data-testid={`product-card-${product.id}`}
    >
      <article
        className="mt-tilt mt-glass mt-rainbow-edge group relative overflow-hidden rounded-2xl sm:rounded-[22px] p-2.5 sm:p-5 lg:p-6 opacity-100"
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ transitionDelay: `${index * 60}ms` }}
      >
        <span aria-hidden="true" className="pointer-events-none absolute -inset-1 rounded-[24px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(0,170,255,0.18), transparent 70%)" }} />

        <div
          className="mt-figure aspect-[4/5] relative flex items-center justify-center"
          style={{ background: `linear-gradient(${angle}deg, #1c1c22 0%, #0a0a0c 55%, #000 100%)` }}
        >
          <span
            aria-hidden="true"
            className="font-display font-semibold select-none"
            style={{
              color: "#F5F5F5", opacity: 0.2,
              fontSize: "clamp(4.5rem, 10vw, 8rem)", letterSpacing: "-0.06em",
              textShadow: "0 2px 30px rgba(255,255,255,0.08)",
            }}
          >
            MT
          </span>
          {product.image && (
            <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover rounded-[14px]" loading="lazy" onError={(e)=>{e.currentTarget.style.display="none";}} />
          )}

          <span aria-hidden="true" className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(180deg, rgba(255,255,255,0.7) 0 1px, transparent 1px 3px)",
          }} />

          {product.tag && (
            <span
              className="absolute top-2 left-2 sm:top-4 sm:left-4 mt-glass px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-mono text-[0.5rem] sm:text-[0.6rem] tracking-mt uppercase"
              style={{ color: ts.color, boxShadow: ts.shadow, borderColor: "rgba(255,255,255,0.18)" }}
              data-testid={`product-tag-${product.id}`}
            >
              {product.tag}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product); toast.success(liked ? `Removed from wishlist` : `Saved to wishlist`, { duration: 1400 }); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 mt-glass"
            style={{
              border: "1px solid rgba(255,255,255,0.18)",
              color: liked ? "#FF4D6D" : "rgba(255,255,255,0.85)",
              boxShadow: liked ? "0 0 14px rgba(255,77,109,0.45)" : "none",
            }}
            aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
            data-testid={`wishlist-${product.id}`}
          >
            <Heart size={15} fill={liked ? "#FF4D6D" : "transparent"} strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 mt-btn opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
            data-testid={`add-to-cart-${product.id}`}
          >
            Add to Cart
          </button>
        </div>

        <div className="mt-tilt-inner pt-2.5 sm:pt-5">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1.5 sm:gap-4">
            <div>
              <h3 className="font-display text-sm sm:text-xl lg:text-2xl font-light leading-tight" style={{ color: "var(--mt-text)" }}>
                {product.name}
              </h3>
              <p className="mt-0.5 sm:mt-1 font-mono text-[0.55rem] sm:text-[0.66rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                {product.category}
              </p>
            </div>
            <div className="text-left sm:text-right">
              <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap sm:justify-end" data-testid={`product-price-${product.id}`}>
                {product.sale_price ? (<>
                  <span className="font-body font-bold text-sm sm:text-base" style={{ color: "#8B0000" }}>{formatINR(product.sale_price)}</span>
                  <span className="font-body text-xs sm:text-sm line-through" style={{ color: "var(--mt-text-muted)" }}>{formatINR(product.price)}</span>
                </>) : (
                  <span className="font-body font-bold text-sm sm:text-base" style={{ color: "var(--mt-text)" }}>{formatINR(product.price)}</span>
                )}
              </div>
              <span className="mt-blood-line mt-2 ml-auto block" aria-hidden="true" style={{ width: 32 }} />
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
